/**
 * Ohio County → DODD Cost-of-Doing-Business (CODB) Tier Mapping.
 *
 * Source: Ohio Administrative Code rule 5123-9-14, Appendix C, revised
 * 2026-02-20. 88 Ohio counties grouped into CODB Categories 1-8 (1 =
 * lowest cost area, 8 = highest cost area).
 *
 * The CODB tier follows the PATIENT's county of residence (where the
 * DODD service is delivered), not where the agency is headquartered.
 * Stay in sync with backend/config/ohio_county_codb.py.
 */

// Counties grouped by CODB category. The flat lookup map below is
// derived from this so the two never drift apart.
export const OHIO_CODB_CATEGORIES = {
  1: ['Adams', 'Athens', 'Belmont', 'Gallia', 'Guernsey', 'Harrison',
      'Jefferson', 'Meigs', 'Monroe', 'Pike', 'Ross', 'Scioto',
      'Tuscarawas', 'Vinton', 'Washington'],
  2: ['Carroll', 'Crawford', 'Defiance', 'Highland', 'Hocking', 'Jackson',
      'Lawrence', 'Mercer', 'Morgan', 'Muskingum', 'Noble', 'Paulding',
      'Perry', 'Van Wert', 'Wyandot'],
  3: ['Allen', 'Auglaize', 'Brown', 'Clinton', 'Columbiana', 'Coshocton',
      'Fayette', 'Hancock', 'Holmes', 'Knox', 'Marion', 'Morrow',
      'Putnam', 'Richland', 'Seneca', 'Shelby', 'Williams'],
  4: ['Ashland', 'Darke', 'Erie', 'Fairfield', 'Fulton', 'Hardin',
      'Henry', 'Huron', 'Licking', 'Logan', 'Mahoning', 'Pickaway',
      'Sandusky', 'Stark', 'Trumbull', 'Wood'],
  5: ['Ashtabula', 'Champaign', 'Clark', 'Delaware', 'Greene', 'Lucas',
      'Madison', 'Miami', 'Montgomery', 'Ottawa', 'Preble', 'Union',
      'Wayne'],
  6: ['Clermont', 'Franklin', 'Geauga', 'Lake', 'Lorain', 'Medina',
      'Portage', 'Summit'],
  7: ['Butler', 'Cuyahoga', 'Warren'],
  8: ['Hamilton'],
};

const _normalize = (name) => {
  if (!name || typeof name !== 'string') return '';
  let cleaned = name.trim().toLowerCase();
  if (cleaned.endsWith(' county')) cleaned = cleaned.slice(0, -7).trim();
  return cleaned;
};

// Flat county→tier lookup map. Lowercased keys for case-insensitive
// lookup. Built from OHIO_CODB_CATEGORIES so a county shift only
// requires editing one place.
const _flatMap = Object.entries(OHIO_CODB_CATEGORIES).reduce((acc, [tier, counties]) => {
  counties.forEach((c) => { acc[_normalize(c)] = Number(tier); });
  return acc;
}, {});

/**
 * Returns the DODD CODB tier (1-8) for the given Ohio county, or null
 * if the county is missing or not in Ohio. Tolerant of casing and the
 * " County" suffix (e.g., "Franklin County" → 6).
 */
export const countyToCodbTier = (county) => {
  if (!county) return null;
  return _flatMap[_normalize(county)] || null;
};

// Total county count — Ohio has 88.
export const OHIO_COUNTY_COUNT = Object.values(OHIO_CODB_CATEGORIES)
  .reduce((sum, list) => sum + list.length, 0);
